/**
 * Public custom hostname → team binding for end-user flows.
 * Firestore: custom_domains. Rules should allow public read for hostname lookup.
 */

// PERFORMANCE: firebase/firestore is dynamically imported inside the actions
// below. This module is registered eagerly in the store, so a static import
// here would pull all of Firestore (~350KB) into the entry bundle.
import type { CustomDomainDoc } from "@/helpers/customDomain";
import {
  customDomainPublicBrandingNeedsTeamFallback,
  isPrimaryMycultureHostname,
  isServingCustomHostnameDocStatus,
  isSuspendedCustomHostnameDocStatus,
  membershipTeamIdsFromUser,
  mergePublicBrandingWithTeamFields,
  normalizeHostname,
} from "@/helpers/customDomain";
import helpers from "@/helpers/global";
import { loadElementPlusServiceStyles } from "@/utils/styleLoader";

// Deploy tracing: set VUE_APP_DEBUG_CUSTOM_HOST=1 and restore body below.
// const DEBUG_CUSTOM_HOST =
//   process.env.VUE_APP_DEBUG_CUSTOM_HOST === "1" ||
//   process.env.VUE_APP_DEBUG_CUSTOM_HOST === "on";

function dbgCustomHost(
  message: string,
  details?: Record<string, string | boolean | number | null | undefined>
): void {
  void message;
  void details;
  // if (!DEBUG_CUSTOM_HOST) return;
  // if (details && Object.keys(details).length > 0) {
  //   console.info("[customHost]", message, details);
  // } else {
  //   console.info("[customHost]", message);
  // }
}

/** Always on: use when hostname bootstrap fails so deploy tests show errors without DEBUG flag. */
function errCustomHost(
  message: string,
  details?: Record<string, string | boolean | number | null | undefined>
): void {
  if (details && Object.keys(details).length > 0) {
    console.error("[customHost]", message, details);
  } else {
    console.error("[customHost]", message);
  }
}

/** Success-path logging disabled — restore body to log via console.info again. */
function okWithTeam(
  message: string,
  entry: string,
  rootGetters: Record<string, unknown> | undefined,
  extra?: Record<string, string | boolean | number | null | undefined>
): void {
  void message;
  void entry;
  void rootGetters;
  void extra;
}

export type CustomHostBootBlockKind =
  | null
  | "not_configured"
  | "inactive"
  | "error";

export interface CustomHostBinding {
  teamId: string;
  hostname: string;
  publicBranding?: CustomDomainDoc["publicBranding"];
}

interface State {
  binding: CustomHostBinding | null;
  resolveAttempted: boolean;
  resolveError: string | null;
  bootBlockKind: CustomHostBootBlockKind;
}

function normalizeHost(h: string): string {
  return String(h || "")
    .split(":")[0]
    .trim()
    .toLowerCase();
}

function isLocalDevHost(h: string): boolean {
  return (
    h === "localhost" ||
    h === "127.0.0.1" ||
    h.endsWith(".local") ||
    h.startsWith("192.168.")
  );
}

function getDefaultAppHostMatch(
  h: string
): false | "auth_domain" | "primary_app_hostnames" {
  const auth = process.env.VUE_APP_FIREBASE_AUTH_DOMAIN || "";
  if (auth) {
    const host = auth.split(":")[0].toLowerCase();
    if (h === host) return "auth_domain";
  }
  const extra = process.env.VUE_APP_PRIMARY_APP_HOSTNAMES || "";
  if (extra) {
    const set = new Set(
      extra.split(",").map((s) => s.trim().toLowerCase()).filter(Boolean)
    );
    if (set.has(h)) return "primary_app_hostnames";
  }
  return false;
}

/**
 * True if `userEmail` matches an active row in Firestore `teams/{id}.members`.
 */
function isUserInTeamMembersDoc(
  userEmailRaw: string,
  team: Record<string, unknown> | null | undefined
): boolean {
  if (!team || team.deleted === true) return false;
  const want = helpers.filterEmail(userEmailRaw);
  if (!want) return false;
  const raw = team.members;
  if (!Array.isArray(raw)) return false;
  for (const m of raw) {
    if (!m || typeof m !== "object") continue;
    const row = m as { email?: string; active?: boolean };
    const em = helpers.filterEmail(String(row.email || ""));
    if (em !== want) continue;
    if (row.active === false) continue;
    return true;
  }
  return false;
}

/** Store + user membership snapshot for console (deploy debugging). */
function teamContextSnapshot(rootGetters: Record<string, unknown> | undefined): Record<
  string,
  string | boolean | number | null | undefined
> {
  const user = rootGetters?.["user/user"] as Record<string, unknown> | null;
  const active = rootGetters?.["team/activeTeamId"];
  const ids = membershipTeamIdsFromUser(user);
  return {
    storeActiveTeamId:
      active != null && String(active) !== "" ? String(active) : "(empty)",
    userProfileTeamId:
      user && String(user.teamId ?? "").trim() !== ""
        ? String(user.teamId)
        : user
          ? "(empty)"
          : "(no user)",
    membershipTeamIds: ids.length > 0 ? ids.join(",") : "(empty)",
    membershipCount: ids.length,
    loggedIn: !!user?.email,
  };
}

function errWithTeam(
  message: string,
  entry: string,
  rootGetters: Record<string, unknown> | undefined,
  extra?: Record<string, string | boolean | number | null | undefined>
): void {
  errCustomHost(message, {
    entry,
    ...teamContextSnapshot(rootGetters),
    ...extra,
  });
}

export default {
  namespaced: true,
  state: (): State => ({
    binding: null,
    resolveAttempted: false,
    resolveError: null,
    bootBlockKind: null,
  }),
  mutations: {
    binding(state: State, v: CustomHostBinding | null): void {
      state.binding = v;
    },
    resolveAttempted(state: State, v: boolean): void {
      state.resolveAttempted = v;
    },
    resolveError(state: State, v: string | null): void {
      state.resolveError = v;
    },
    bootBlockKind(state: State, v: CustomHostBootBlockKind): void {
      state.bootBlockKind = v;
    },
  },
  getters: {
    binding(state: State): CustomHostBinding | null {
      return state.binding;
    },
    isCustomHost(state: State): boolean {
      return !!state.binding?.teamId;
    },
    resolveAttempted(state: State): boolean {
      return state.resolveAttempted;
    },
    resolveError(state: State): string | null {
      return state.resolveError;
    },
    bootBlockKind(state: State): CustomHostBootBlockKind {
      return state.bootBlockKind;
    },
    bootBlocked(state: State): boolean {
      return state.bootBlockKind != null;
    },
  },
  actions: {
    async resolveFromWindowHostname({
      commit,
      rootGetters,
    }: {
      commit: any;
      rootGetters: Record<string, unknown>;
    }): Promise<void> {
      const rg = rootGetters;
      const hostname = normalizeHost(
        typeof window !== "undefined" ? window.location.hostname : ""
      );
      commit("resolveError", null);
      commit("bootBlockKind", null);

      okWithTeam("BOOT_RESOLVE_START", "boot_start", rg, {
        hostname: hostname || "(empty)",
      });

      dbgCustomHost("resolveFromWindowHostname", {
        hostname: hostname || "(empty)",
        href:
          typeof window !== "undefined" ? window.location.href : "(no window)",
      });

      if (!hostname) {
        dbgCustomHost("canonical skip: empty hostname");
        okWithTeam("BOOT_OK_PRIMARY_EMPTY_HOSTNAME", "primary_empty_hostname", rg, {});
        commit("binding", null);
        commit("resolveAttempted", true);
        return;
      }

      if (isLocalDevHost(hostname)) {
        dbgCustomHost("canonical skip: local_dev", { hostname });
        okWithTeam("BOOT_OK_PRIMARY_LOCAL_DEV", "primary_local_dev", rg, { hostname });
        commit("binding", null);
        commit("resolveAttempted", true);
        return;
      }

      const defaultMatch = getDefaultAppHostMatch(hostname);
      if (defaultMatch) {
        dbgCustomHost("canonical skip: default_app", {
          hostname,
          reason: defaultMatch,
        });
        okWithTeam("BOOT_OK_PRIMARY_CANONICAL", "primary_canonical_app", rg, {
          hostname,
          via: defaultMatch,
        });
        commit("binding", null);
        commit("resolveAttempted", true);
        return;
      }

      if (isPrimaryMycultureHostname(hostname)) {
        dbgCustomHost("canonical skip: primary_myculture_com", { hostname });
        okWithTeam("BOOT_OK_PRIMARY_MYCO_DOT_COM", "primary_myculture_com", rg, {
          hostname,
        });
        commit("binding", null);
        commit("resolveAttempted", true);
        return;
      }

      try {
        dbgCustomHost("firestore lookup start", { hostname });
        const firestoreInit = await import("@/utils/firestoreInit");
        firestoreInit.ensureFirestoreInit();
        const { collection, query, where, limit, getDocs, doc, getDoc  } = await import("firebase/firestore");
        const { getAppFirestore } = await import("@/utils/appFirestore");
        const fs = getAppFirestore();
        let resolved: CustomDomainDoc | null = null;
        let rowHostname = hostname;

        const byIdSnap = await getDoc(doc(fs, "custom_domains", hostname));
        if (byIdSnap.exists()) {
          const d = byIdSnap.data() as CustomDomainDoc;
          if (d && d.deleted !== true) {
            resolved = d;
          }
        }
        dbgCustomHost("firestore getDoc(custom_domains/{hostname})", {
          hostname,
          byIdHit: !!resolved,
        });

        if (!resolved) {
          const q = query(
            collection(fs, "custom_domains"),
            where("hostname", "==", hostname),
            limit(15)
          );
          const snap = await getDocs(q);
          const row = snap.docs
            .map((dDoc) => ({ id: dDoc.id, data: dDoc.data() as CustomDomainDoc }))
            .find((r) => r.data && r.data.deleted !== true);
          if (row) {
            resolved = row.data;
            rowHostname = row.data.hostname || hostname;
          }
          dbgCustomHost("firestore query hostname==", {
            hostname,
            docCount: snap.docs.length,
            picked: !!row,
          });
        }

        if (!resolved || !resolved.teamId) {
          dbgCustomHost("boot not_configured (no row or no teamId)", {
            hostname,
            hasRow: !!resolved,
          });
          errWithTeam("BOOT_BLOCKED_NOT_CONFIGURED", "blocked_not_configured", rg, {
            hostname,
            hasRow: !!resolved,
            hint: resolved
              ? "custom_domains doc missing teamId"
              : "no custom_domains bind for hostname (check Firestore + rules)",
          });
          commit("binding", null);
          commit("bootBlockKind", "not_configured");
          return;
        }

        if (isSuspendedCustomHostnameDocStatus(resolved.status)) {
          dbgCustomHost("boot inactive (suspended)", {
            hostname,
            status: String(resolved.status ?? ""),
          });
          errWithTeam("BOOT_BLOCKED_INACTIVE_SUSPENDED", "blocked_inactive", rg, {
            hostname,
            status: String(resolved.status ?? ""),
          });
          commit("binding", null);
          commit("bootBlockKind", "inactive");
          return;
        }

        if (!isServingCustomHostnameDocStatus(resolved.status)) {
          dbgCustomHost("boot not_configured (non-serving status)", {
            hostname,
            status: String(resolved.status ?? ""),
          });
          errWithTeam(
            "BOOT_BLOCKED_NOT_CONFIGURED_BAD_STATUS",
            "blocked_bad_status",
            rg,
            {
              hostname,
              status: String(resolved.status ?? ""),
              hint:
                "expected active/revalidation_warning — see helpers isServingCustomHostnameDocStatus",
            }
          );
          commit("binding", null);
          commit("bootBlockKind", "not_configured");
          return;
        }

        const boundHost = normalizeHostname(rowHostname) || hostname;
        dbgCustomHost("binding ok", {
          hostname,
          boundHost,
          teamId: String(resolved.teamId),
          status: String(resolved.status ?? ""),
        });
        okWithTeam(
          "BOOT_OK_CUSTOM_HOST_BOUND",
          "custom_domain_firestore_bound",
          rg,
          {
            hostname,
            boundHost,
            boundTeamIdFromDomain: String(resolved.teamId),
            docStatus: String(resolved.status ?? ""),
          }
        );

        let publicBranding = resolved.publicBranding ?? undefined;
        if (
          customDomainPublicBrandingNeedsTeamFallback(publicBranding) &&
          resolved.teamId
        ) {
          try {
            const teamSnap = await getDoc(
              doc(fs, "teams", String(resolved.teamId))
            );
            if (teamSnap.exists()) {
              const merged = mergePublicBrandingWithTeamFields(
                publicBranding,
                teamSnap.data() as Record<string, unknown>
              );
              if (merged !== undefined) {
                publicBranding = merged;
              }
            }
          } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : String(e);
            dbgCustomHost("team branding fallback failed", {
              teamId: String(resolved.teamId),
              message: msg.slice(0, 240),
            });
          }
        }

        commit("binding", {
          teamId: String(resolved.teamId),
          hostname: boundHost,
          publicBranding: publicBranding ?? null,
        });
        commit("bootBlockKind", null);
      } catch (e: any) {
        const msg = e?.message || String(e);
        dbgCustomHost("boot error", { message: msg });
        errWithTeam(
          "BOOT_BLOCKED_FIRESTORE_OR_CLIENT_ERROR",
          "blocked_firestore_or_client",
          rg,
          {
            hostname:
              typeof window !== "undefined"
                ? normalizeHost(window.location.hostname)
                : "(no window)",
            message: msg,
            hint:
              "permission_denied ⇒ deploy Firestore rules for custom_domains read; offline ⇒ network",
          }
        );
        commit("resolveError", msg);
        commit("binding", null);
        commit("bootBlockKind", "error");
      } finally {
        commit("resolveAttempted", true);
      }
    },

    /**
     * First active custom_domains hostname for share links (admin on canonical host).
     */
    async fetchOutboundHostnameForTeam(
      { rootGetters }: { rootGetters: Record<string, unknown> },
      teamId: string
    ): Promise<string | null> {
      if (!teamId) return null;
      try {
        const firestoreInit = await import("@/utils/firestoreInit");
        firestoreInit.ensureFirestoreInit();
        const { collection, query, where, limit, getDocs  } = await import("firebase/firestore");
        const { getAppFirestore } = await import("@/utils/appFirestore");
        const fs = getAppFirestore();
        const q = query(
          collection(fs, "custom_domains"),
          where("teamId", "==", teamId),
          limit(20)
        );
        const snap = await getDocs(q);
        for (const dDoc of snap.docs) {
          const data = dDoc.data() as CustomDomainDoc;
          if (data?.deleted === true) continue;
          if (!isServingCustomHostnameDocStatus(data.status)) continue;
          const h = data.hostname ? normalizeHostname(data.hostname) : "";
          if (h) {
            okWithTeam("FETCH_OUTBOUND_HOSTNAME_OK", "share_link_outbound_host", rootGetters, {
              requestedTeamId: teamId,
              outboundHost: h,
            });
            return h;
          }
        }
        okWithTeam(
          "FETCH_OUTBOUND_HOSTNAME_NONE",
          "share_link_no_outbound_row",
          rootGetters,
          {
            requestedTeamId: teamId,
            hint: "no serving custom_domains row for team; links use canonical host",
          }
        );
      } catch (e: any) {
        const m = e?.message || String(e);
        errWithTeam(
          "FETCH_OUTBOUND_HOSTNAME_FAILED",
          "share_link_outbound_query_failed",
          rootGetters,
          {
            requestedTeamId: teamId,
            message: m,
            hint: "Firestore rules may deny list by teamId; link falls back to canonical host",
          }
        );
      }
      return null;
    },

    /**
     * After user profile is loaded on a custom host: if `user.customDomainId` matches the bound
     * hostname (portal signup lock), allow access. Otherwise require an active row in
     * `teams/{teamId}.members`. Then align active team to the binding.
     *
     * DEPRECATED path (not used yet — profile often lacks full teamIds): infer membership only from
     * `user.teamIds` / `user.teamId` via membershipTeamIdsFromUser(). Future: dedicated membership
     * collection + query instead of reading the full team document here.
     */
    async enforceTeamContext({
      dispatch,
      getters,
      rootGetters,
      commit,
    }: {
      dispatch: any;
      getters: any;
      rootGetters: any;
      commit: any;
    }): Promise<void> {
      const binding = getters.binding as CustomHostBinding | null;
      if (!binding?.teamId) return;

      const user = rootGetters["user/user"] as Record<string, unknown> | null;
      if (!user?.email) return;

      const boundTeam = (await dispatch(
        "team/getTeam",
        { teamId: binding.teamId },
        { root: true }
      )) as Record<string, unknown> | null | undefined;

      if (!boundTeam) {
        errWithTeam(
          "ACCESS_DENIED_BOUND_TEAM_MISSING",
          "access_denied_team_doc_missing",
          rootGetters,
          {
            hostname: binding.hostname,
            boundTeamId: binding.teamId,
            hint: "teams/{binding.teamId} missing or unreadable",
          }
        );
        await loadElementPlusServiceStyles();
        const { ElMessageBox } = await import("@/utils/elementServices");
        const i18n = (await import("@/plugins/vue-i18n")).default;
        try {
          await ElMessageBox.alert(
            i18n.t("customDomain.accessDeniedHostname"),
            i18n.t("Error"),
            { confirmButtonText: i18n.t("btn.Confirm") }
          );
        } catch {
          // dismissed
        }
        const { safeRemoveItem } = await import("@/utils/storage");
        safeRemoveItem("userData");
        safeRemoveItem("cachedCompanyColor");
        safeRemoveItem("cachedCompanyLogoUrl");
        const { getAuth, signOut } = await import("firebase/auth");
        await signOut(getAuth());
        window.location.replace("/login");
        return;
      }

      // DEPRECATED: membershipTeamIdsFromUser(user) + user.teamIds / user.teamId — profile may not
      // list every team yet. Prefer checking bound team doc `.members` above; future: membership collection + query.
      // const ids = membershipTeamIdsFromUser(user);
      // if (!ids.includes(binding.teamId)) { ... deny ... }

      /** Portal signup lock: matching customDomainId ⇒ allow without re-checking team.members */
      const profileDomainRaw = String(
        (user as Record<string, unknown>).customDomainId ?? ""
      ).trim();
      let passedCustomDomainSignupGate = false;
      if (profileDomainRaw) {
        const boundHost = normalizeHostname(binding.hostname);
        const profileHost = normalizeHostname(profileDomainRaw);
        passedCustomDomainSignupGate = profileHost === boundHost;
      }

      if (!passedCustomDomainSignupGate) {
        if (!isUserInTeamMembersDoc(String(user.email || ""), boundTeam)) {
          errWithTeam(
            "ACCESS_DENIED_WRONG_TEAM_FOR_HOST",
            "access_denied_not_member",
            rootGetters,
            {
              hostname: binding.hostname,
              boundTeamId: binding.teamId,
              hint:
                "user email must appear as an active row in teams/{boundTeam}.members for this custom host",
            }
          );
          await loadElementPlusServiceStyles();
          const { ElMessageBox } = await import("@/utils/elementServices");
          const i18n = (await import("@/plugins/vue-i18n")).default;
          try {
            await ElMessageBox.alert(
              i18n.t("customDomain.accessDeniedHostname"),
              i18n.t("Error"),
              { confirmButtonText: i18n.t("btn.Confirm") }
            );
          } catch {
            // dismissed
          }
          const { safeRemoveItem } = await import("@/utils/storage");
          safeRemoveItem("userData");
          safeRemoveItem("cachedCompanyColor");
          const { getAuth, signOut } = await import("firebase/auth");
          await signOut(getAuth());
          window.location.replace("/login");
          return;
        }
      }

      const priorUserProfileTeamId =
        String(user.teamId || "").trim() || "(empty)";
      const didCallChangeTeam = priorUserProfileTeamId !== binding.teamId;

      if (didCallChangeTeam) {
        await dispatch("user/changeTeam", binding.teamId, { root: true });
      }
      commit("team/activeTeamId", binding.teamId, { root: true });
      await dispatch(
        "team/getTeam",
        { teamId: binding.teamId },
        { root: true }
      );
      okWithTeam(
        "ACCESS_OK_TEAM_ALIGNED",
        didCallChangeTeam
          ? "access_ok_switched_to_binding_team"
          : "access_ok_already_on_binding_team",
        rootGetters,
        {
          hostname: binding.hostname,
          boundTeamId: binding.teamId,
          priorUserProfileTeamId,
          didCallChangeTeam,
        }
      );
    },
  },
};
